import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = {
  class: "container",
  style: {"margin-top":"80px","padding-top":"40px"}
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-lg-6 text-right align-self-end" }
const _hoisted_4 = { class: "mt-3" }
const _hoisted_5 = { class: "table table-bordered table-hover" }
const _hoisted_6 = { class: "dropdown" }
const _hoisted_7 = { class: "dropdown-menu dropdown-menu-right" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { colspan: "3" }
const _hoisted_11 = { class: "dropdown" }
const _hoisted_12 = { class: "dropdown-menu dropdown-menu-right" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "dropdown" }
const _hoisted_16 = { class: "dropdown-menu dropdown-menu-right" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = ["onClick"]

import { computed, onMounted, watch, shallowReactive, Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { guid } from '@common/Guid'

    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import CostSubtotalEditor from "@Quest/Costs/Components/CostSubtotalEditor.vue"
    import ExpenseAccountEditor from "@Quest/Costs/Components/ExpenseAccountEditor.vue"

    import type {
        CreateCostSubtotalEventPayload,
        RenameCostSubtotalEventPayload
    } from '@Quest/Costs/Components/CostSubtotalEditor'

    import type {
        CreateExpenseAccountEventPayload,
        UpdateCostAccountEventPayload
    } from '@Quest/Costs/Components/ExpenseAccountEditor'

    import
        {
            CostSubtotalEditorViewModel
        } from '@Quest/Costs/Components/CostSubtotalEditor'

    import
        {
            ExpenseAccountEditorViewModel
        } from '@Quest/Costs/Components/ExpenseAccountEditor'

    import type {
        CostAccountReadModel,
        CostSubtotalReadModel,
        CostsReadModel
    } from '@Quest/Costs/readmodels'

    import
        {
            useManageCostsPageQueryV1
        } from '@Quest/Costs/queries-vue-composables'

    import
        {
            useCreateExpenseAccountCommandV1,
            useUpdateCostAccountCommandV1,
            useDeleteExpenseAccountCommandV1,
            useCreateCostSubtotalCommandV1,
            useRenameCostSubtotalCommandV1,
            useDeleteCostSubtotalCommandV1
        } from '@Quest/Costs/commands-vue-composables'

    /* -------------------- Route -------------------- */

    
export default /*@__PURE__*/_defineComponent({
  __name: 'CostsPage',
  setup(__props) {

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    /* -------------------- Editors -------------------- */

    const costSubtotalEditor = shallowReactive(new CostSubtotalEditorViewModel())
    const expenseAccountEditor = shallowReactive(new ExpenseAccountEditorViewModel())

    /* -------------------- Query -------------------- */

    const {
        runManageCostsPageQueryV1,
        modelManageCostsPageQueryV1
    } = useManageCostsPageQueryV1(workspaceId, strategyId)

    const rm = modelManageCostsPageQueryV1 as Ref<CostsReadModel | null>

    /* -------------------- Derived State -------------------- */

    const costs = computed(() => rm.value?.Costs ?? [])
    const subtotals = computed(() => rm.value?.Subtotals ?? [])
    const standaloneCosts = computed<CostAccountReadModel[]>(() =>
        costs.value.filter(a => a.IsStandalone)
    )

    /* -------------------- Commands -------------------- */

    const {
        invokeCreateExpenseAccountCommandV1,
        succeedCreateExpenseAccountCommandV1
    } = useCreateExpenseAccountCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateCostAccountCommandV1,
        succeedUpdateCostAccountCommandV1
    } = useUpdateCostAccountCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteExpenseAccountCommandV1,
        succeedDeleteExpenseAccountCommandV1
    } = useDeleteExpenseAccountCommandV1(workspaceId, strategyId)

    const {
        invokeCreateCostSubtotalCommandV1,
        succeedCreateCostSubtotalCommandV1
    } = useCreateCostSubtotalCommandV1(workspaceId, strategyId)

    const {
        invokeRenameCostSubtotalCommandV1,
        succeedRenameCostSubtotalCommandV1
    } = useRenameCostSubtotalCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteCostSubtotalCommandV1,
        succeedDeleteCostSubtotalCommandV1
    } = useDeleteCostSubtotalCommandV1(workspaceId, strategyId)

    /* -------------------- Success Watcher -------------------- */

    watch(
        [
            succeedCreateExpenseAccountCommandV1,
            succeedUpdateCostAccountCommandV1,
            succeedDeleteExpenseAccountCommandV1
        ],
        ([create, update, del], [prevCreate, prevUpdate, prevDel]) =>
        {
            if (
                (create && !prevCreate) ||
                (update && !prevUpdate) ||
                (del && !prevDel)
            )
            {
                runManageCostsPageQueryV1()
                expenseAccountEditor.close()
            }
        }
    )

    watch(
        [
            succeedCreateCostSubtotalCommandV1,
            succeedRenameCostSubtotalCommandV1,
            succeedDeleteCostSubtotalCommandV1
        ],
        ([create, rename, del], [prevCreate, prevRename, prevDel]) =>
        {
            if (
                (create && !prevCreate) ||
                (rename && !prevRename) ||
                (del && !prevDel)
            )
            {
                runManageCostsPageQueryV1()
                costSubtotalEditor.close()
            }
        }
    )

    /* -------------------- UI Actions -------------------- */

    function showExpenseCreator(parentSubtotalId: string | null)
    {
        expenseAccountEditor.showCreator(parentSubtotalId, subtotals.value)
    }

    function showExpenseEditor(e: CostAccountReadModel)
    {
        expenseAccountEditor.showEditor(
            e.Id,
            e.Name,
            e.Classification,
            e.SubtotalId,
            e.CanEditName,
            e.CanEditClassification,
            subtotals.value
        )
    }

    function showSubtotalEditor(s: CostSubtotalReadModel)
    {
        costSubtotalEditor.showEditor(s.Id, s.Name)
    }

    function showSubtotalCreator()
    {
        costSubtotalEditor.showCreator()
    }

    function handleCreateExpenseAccountCommand(e: CreateExpenseAccountEventPayload)
    {
        invokeCreateExpenseAccountCommandV1({
            id: guid(),
            name: e.name,
            description: null,
            classification: e.classification,
            subtotalId: e.subtotalId
        })
    }

    function hanldeUpdateCostAccountCommand(e: UpdateCostAccountEventPayload)
    {
        invokeUpdateCostAccountCommandV1({
            id: e.id,
            name: e.name,
            description: null,
            classification: e.classification,
            subtotalId: e.subtotalId
        })
    }

    function handleDeleteExpenseAccountCommand(expense: CostAccountReadModel)
    {
        if (!expense.CanDelete)
        {
            alert("Can't delete this type of cost")
            return
        }

        if (confirm("Are you sure?"))
        {
            invokeDeleteExpenseAccountCommandV1({
                id: expense.Id
            })
        }
    }

    function handleCreateCostSubtotalCommand(e: CreateCostSubtotalEventPayload)
    {
        invokeCreateCostSubtotalCommandV1({
            id: guid(),
            name: e.name
        })
    }

    function handleRenameCostSubtotalCommand(e: RenameCostSubtotalEventPayload)
    {
        invokeRenameCostSubtotalCommandV1({
            id: e.id,
            name: e.name
        })
    }

    function handleDeleteCostSubtotalCommand(subtotal: CostSubtotalReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeleteCostSubtotalCommandV1({
                id: subtotal.Id
            })
        }
    }

    /* -------------------- Lifecycle -------------------- */

    onMounted(() =>
    {
        document.title = "Costs | TransparentChoice"
        runManageCostsPageQueryV1()
    })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(CostSubtotalEditor, {
      viewModel: _unref(costSubtotalEditor),
      onCreateSubtotal: handleCreateCostSubtotalCommand,
      onRenameSubtotal: handleRenameCostSubtotalCommand
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(ExpenseAccountEditor, {
      viewModel: _unref(expenseAccountEditor),
      onCreateExpense: handleCreateExpenseAccountCommand,
      onUpdateCost: hanldeUpdateCostAccountCommand
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(CorporateNavigation, { ActivePage: "strategy" }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "col-lg-6" }, [
          _createElementVNode("h1", { style: {"font-size":"2em","font-weight":"300","color":"#33475b","margin":"0"} }, "Costs")
        ], -1 /* CACHED */)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            role: "button",
            class: "btn btn-outline-secondary mr-2",
            style: {"padding":"0.7em 2em"},
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showSubtotalCreator()))
          }, "Create subtotal"),
          _createElementVNode("button", {
            role: "button",
            class: "btn btn-success",
            style: {"padding":"0.7em 2em"},
            onClick: _cache[1] || (_cache[1] = ($event: any) => (showExpenseCreator(null)))
          }, "Create expense")
        ])
      ]),
      _cache[9] || (_cache[9] = _createElementVNode("hr", { style: {"border-color":"#bbb","margin":"20px 0 60px"} }, null, -1 /* CACHED */)),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("table", _hoisted_5, [
          _cache[8] || (_cache[8] = _createElementVNode("thead", null, [
            _createElementVNode("tr", null, [
              _createElementVNode("th"),
              _createElementVNode("th", { style: {"width":"120px"} }),
              _createElementVNode("th", { style: {"width":"120px"} }),
              _createElementVNode("th", { style: {"width":"120px"} })
            ])
          ], -1 /* CACHED */)),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(standaloneCosts.value, (cost) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: cost.Id
              }, [
                _createElementVNode("td", null, [
                  _createElementVNode("strong", null, _toDisplayString(cost.Name), 1 /* TEXT */),
                  _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1 /* CACHED */))
                ]),
                _createElementVNode("td", null, _toDisplayString(cost.AccountType), 1 /* TEXT */),
                _createElementVNode("td", null, _toDisplayString(cost.Classification), 1 /* TEXT */),
                _createElementVNode("td", null, [
                  _createElementVNode("div", _hoisted_6, [
                    _cache[4] || (_cache[4] = _createElementVNode("button", {
                      type: "button",
                      class: "btn btn-outline-secondary dropdown-toggle",
                      "data-toggle": "dropdown"
                    }, " Actions ", -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("a", {
                        class: "dropdown-item",
                        onClick: ($event: any) => (showExpenseEditor(cost))
                      }, "Edit", 8 /* PROPS */, _hoisted_8),
                      _createElementVNode("a", {
                        class: "dropdown-item",
                        onClick: ($event: any) => (handleDeleteExpenseAccountCommand(cost))
                      }, "Delete", 8 /* PROPS */, _hoisted_9)
                    ])
                  ])
                ])
              ]))
            }), 128 /* KEYED_FRAGMENT */))
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subtotals.value, (sub) => {
            return (_openBlock(), _createElementBlock("tbody", {
              key: sub.Id
            }, [
              _createElementVNode("tr", null, [
                _createElementVNode("td", _hoisted_10, _toDisplayString(sub.Name), 1 /* TEXT */),
                _createElementVNode("td", null, [
                  _createElementVNode("div", _hoisted_11, [
                    _cache[5] || (_cache[5] = _createElementVNode("button", {
                      type: "button",
                      class: "btn btn-outline-secondary dropdown-toggle",
                      "data-toggle": "dropdown"
                    }, " Actions ", -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("a", {
                        class: "dropdown-item",
                        onClick: ($event: any) => (showSubtotalEditor(sub))
                      }, "Edit", 8 /* PROPS */, _hoisted_13),
                      _createElementVNode("a", {
                        class: "dropdown-item",
                        onClick: ($event: any) => (handleDeleteCostSubtotalCommand(sub))
                      }, "Delete", 8 /* PROPS */, _hoisted_14)
                    ])
                  ])
                ])
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sub.Costs, (cost) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: cost.Id
                }, [
                  _createElementVNode("td", null, [
                    _createElementVNode("strong", null, _toDisplayString(cost.Name), 1 /* TEXT */),
                    _cache[6] || (_cache[6] = _createElementVNode("br", null, null, -1 /* CACHED */))
                  ]),
                  _createElementVNode("td", null, _toDisplayString(cost.AccountType), 1 /* TEXT */),
                  _createElementVNode("td", null, _toDisplayString(cost.Classification), 1 /* TEXT */),
                  _createElementVNode("td", null, [
                    _createElementVNode("div", _hoisted_15, [
                      _cache[7] || (_cache[7] = _createElementVNode("button", {
                        type: "button",
                        class: "btn btn-outline-secondary dropdown-toggle",
                        "data-toggle": "dropdown"
                      }, " Actions ", -1 /* CACHED */)),
                      _createElementVNode("div", _hoisted_16, [
                        _createElementVNode("a", {
                          class: "dropdown-item",
                          onClick: ($event: any) => (showExpenseEditor(cost))
                        }, "Edit", 8 /* PROPS */, _hoisted_17),
                        _createElementVNode("a", {
                          class: "dropdown-item",
                          onClick: ($event: any) => (handleDeleteExpenseAccountCommand(cost))
                        }, "Delete", 8 /* PROPS */, _hoisted_18)
                      ])
                    ])
                  ])
                ]))
              }), 128 /* KEYED_FRAGMENT */))
            ]))
          }), 128 /* KEYED_FRAGMENT */))
        ])
      ])
    ])
  ]))
}
}

})